import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, fileUrl } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";

export default function Brands() {
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="brands-page">
      <PageHero
        eyebrow="Our Businesses"
        title="Specialised education brands, built to last."
        lede="RCS builds and operates focused education businesses across two major areas of learning — academic education and music education — with the architecture to grow into new verticals over time."
      />

      <section className="py-24 lg:py-32" data-testid="brands-list">
        <div className="container-rcs space-y-20">
          {brands.map((b, i) => (
            <Reveal key={b.id}>
              <article
                className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`brand-block-${b.slug}`}
              >
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={fileUrl(b.heroImage)}
                    alt={`${b.brandName} — ${b.category}`}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover"
                  />
                </div>
                <div>
                  <span
                    className="inline-block rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
                    style={{ backgroundColor: b.brandColor || "#D4AF37" }}
                  >
                    {b.category}
                  </span>
                  <h2 className="mt-5 font-serif text-3xl text-navy-900 sm:text-4xl">{b.brandName}</h2>
                  {b.tagline && <p className="mt-2 text-sm font-semibold uppercase tracking-[0.15em] text-slate-500">{b.tagline}</p>}
                  <p className="mt-6 text-base leading-relaxed text-slate-600">{b.description}</p>
                  {b.purpose && <p className="mt-4 text-base leading-relaxed text-slate-600">{b.purpose}</p>}
                  {b.learningAreas?.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {b.learningAreas.map((a) => (
                        <span key={a} className="rounded-full border border-navy-900/15 bg-white px-3.5 py-1 text-xs font-semibold text-navy-900">
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-8 flex flex-wrap gap-4">
                    <a href={b.websiteURL} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid={`brands-visit-${b.slug}`}>
                      Explore {b.brandName} <ArrowUpRight className="h-4 w-4" aria-hidden />
                    </a>
                    <Link to={`/brands/${b.slug}`} className="btn-outline-dark" data-testid={`brands-overview-${b.slug}`}>
                      Brand Overview <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-navy-950 py-20" data-testid="brands-future">
        <div className="container-rcs">
          <Section
            dark
            eyebrow="Built for Expansion"
            title="Architecture for the brands of tomorrow."
            lede="RCS is designed as a parent organisation capable of launching and acquiring additional education brands, learning platforms and technology products — without reinventing its foundations."
          />
        </div>
      </section>
    </div>
  );
}
